import {
	requestApiDelete,
	requestApiGet,
	requestApiGetFile,
	requestApiPatch,
	requestApiPost,
} from "../api/BacklogApiEndpoint";
import type { BacklogConfig } from "../contracts/BacklogConfig";
import type { BacklogApiContext } from "../contracts/api/BacklogApiContext";
import type { BacklogResponse } from "../contracts/api/BacklogResponse";
import type { BacklogResponseFile } from "../contracts/api/BacklogResponseFile";
import type { Issue } from "../entities/Issue";
import type { IssueAttachmentFile } from "../entities/IssueAttachmentFile";
import type { IssueComment } from "../entities/IssueComment";
import type { IssueCommentCount } from "../entities/IssueCommentCount";
import type { IssueCommentNotification } from "../entities/IssueCommentNotification";
import type { IssueCount } from "../entities/IssueCount";
import type { SharedFile } from "../entities/SharedFile";
import type { User } from "../entities/User";
import { assert } from "../utils/BacklogUtil";
import type { AddIssueCommentParams } from "./params/AddIssueCommentParams";
import type { AddIssueParams } from "./params/AddIssueParams";
import type { AddIssuecommentNotificationParams } from "./params/AddIssuecommentNotificationParams";
import type { CountIssueCommentParams } from "./params/CountIssueCommentParams";
import type { CountIssueParams } from "./params/CountIssueParams";
import type { DeleteIssueAttachmentParams } from "./params/DeleteIssueAttachmentParams";
import type { DeleteIssueCommentParams } from "./params/DeleteIssueCommentParams";
import type { DeleteIssueParams } from "./params/DeleteIssueParams";
import type { GetIssueCommentListParams } from "./params/GetCommentListParams";
import type { GetIssueAttachmentParams } from "./params/GetIssueAttachmentParams";
import type { GetIssueCommentParams } from "./params/GetIssueCommentParams";
import type { GetIssueListParams } from "./params/GetIssueListParams";
import type { GetIssueParams } from "./params/GetIssueParams";
import type { GetIssueParticipantListParams } from "./params/GetIssueParticipantListParams";
import type { GetListOfIssueAttachmentsParams } from "./params/GetListOfIssueAttachmentsParams";
import type { GetListOfIssueCommentNotificationsParams } from "./params/GetListOfIssueCommentNotificationsParams";
import type { GetListOfIssueLinkedSharedFilesParams } from "./params/GetListOfIssueLinkedSharedFilesParams";
import type { LinkSharedFilesToIssueParams } from "./params/LinkSharedFilesToIssueParams";
import type { RemoveLinkToSharedFileFromIssueParams } from "./params/RemoveLinkToSharedFileFromIssueParams";
import type { UpdateIssueCommentParams } from "./params/UpdateIssueCommentParams";
import type { UpdateIssueParams } from "./params/UpdateIssueParams";

/**
 * Get Issue List 課題一覧の取得
 *
 * Returns list of issues.
 */
export function getIssueList(
	config: BacklogConfig,
	params?: GetIssueListParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Issue[]>> {
	return requestApiGet({
		config,
		path: "issues",
		params,
		context,
	});
}

/**
 * Count Issue 課題数の取得
 *
 * Returns number of issues.
 */
export function countIssue(
	config: BacklogConfig,
	params?: CountIssueParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<IssueCount>> {
	return requestApiGet({
		config,
		path: "issues/count",
		params,
		context: context,
	});
}

/**
 * Add Issue 課題の追加
 *
 * Adds new issue.
 */
export function addIssue(
	config: BacklogConfig,
	params: AddIssueParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Issue>> {
	assert(params);
	return requestApiPost({
		config,
		path: "issues",
		params,
		context,
	});
}

/**
 * Get Issue 課題情報の取得
 *
 * Returns information about issue.
 */
export function getIssue(
	config: BacklogConfig,
	params: GetIssueParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Issue>> {
	assert(params);
	return requestApiGet({
		config,
		path: "issues/:issueIdOrKey",
		params,
		context,
	});
}

/**
 * Update Issue 課題の情報を更新します。
 *
 * Updates information about issue.
 */
export function updateIssue(
	config: BacklogConfig,
	params: UpdateIssueParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Issue>> {
	assert(params);
	return requestApiPatch({
		config,
		path: "issues/:issueIdOrKey",
		params,
		context,
	});
}

/**
 * Delete Issue 課題の削除
 *
 * Deletes issue.
 */
export function deleteIssue(
	config: BacklogConfig,
	params: DeleteIssueParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<Issue>> {
	assert(params);
	return requestApiDelete({
		config,
		path: "issues/:issueIdOrKey",
		params,
		context,
	});
}

/**
 * Get Comment List 課題コメントの取得
 *
 * Returns list of comments in issue.
 */
export function getIssueCommentList(
	config: BacklogConfig,
	params: GetIssueCommentListParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<IssueComment[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "issues/:issueIdOrKey/comments",
		params,
		context,
	});
}

/**
 * Add Comment 課題コメントの追加
 *
 * Adds a comment to the issue.
 */
export function addIssueComment(
	config: BacklogConfig,
	params: AddIssueCommentParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<IssueComment>> {
	assert(params);
	return requestApiPost({
		config,
		path: "issues/:issueIdOrKey/comments",
		params,
		context,
	});
}

/**
 * Count Comment 課題コメント数の取得
 *
 * Returns number of comments in issue.
 */
export function countIssueComment(
	config: BacklogConfig,
	params: CountIssueCommentParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<IssueCommentCount>> {
	assert(params);
	return requestApiGet({
		config,
		path: "issues/:issueIdOrKey/comments/count",
		params,
		context: context,
	});
}

/**
 * Get Comment 課題コメントの取得
 *
 * Returns information about comment.
 */
export function getIssueComment(
	config: BacklogConfig,
	params: GetIssueCommentParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<IssueComment>> {
	assert(params);
	return requestApiGet({
		config,
		path: "issues/:issueIdOrKey/comments/:commentId",
		params,
		context,
	});
}

/**
 * Delete Comment 課題コメントの削除
 *
 * Delete comment.
 *
 * User can delete own comment.
 */
export function deleteIssueComment(
	config: BacklogConfig,
	params: DeleteIssueCommentParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<IssueComment>> {
	assert(params);
	return requestApiDelete({
		config,
		path: "issues/:issueIdOrKey/comments/:commentId",
		params,
		context,
	});
}

/**
 * Update comment 課題コメント情報の更新
 *
 * Updates content of comment.
 *
 * User can update own comment.
 */
export function updateIssueComment(
	config: BacklogConfig,
	params: UpdateIssueCommentParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<IssueComment>> {
	assert(params);
	return requestApiPatch({
		config,
		path: "issues/:issueIdOrKey/comments/:commentId",
		params,
		context,
	});
}

/**
 * Get List of Comment Notifications 課題コメントのお知らせ一覧の取得
 *
 * Returns the list of comment notifications.
 */
export function getListOfIssueCommentNotifications(
	config: BacklogConfig,
	params: GetListOfIssueCommentNotificationsParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<IssueCommentNotification[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "issues/:issueIdOrKey/comments/:commentId/notifications",
		params,
		context,
	});
}

/**
 * Add Comment Notification 課題コメントにお知らせを追加
 *
 * Adds notifications to the comment. Only the user who added the comment can add notifications.
 */
export function addIssuecommentNotification(
	config: BacklogConfig,
	params: AddIssuecommentNotificationParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<IssueComment>> {
	assert(params);
	return requestApiPost({
		config,
		path: "issues/:issueIdOrKey/comments/:commentId/notifications",
		params,
		context,
	});
}

/**
 * Get List of Issue Attachments 課題添付ファイル一覧の取得
 *
 * Returns the list of issue attachments.
 */
export function getListOfIssueAttachments(
	config: BacklogConfig,
	params: GetListOfIssueAttachmentsParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<IssueAttachmentFile[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "issues/:issueIdOrKey/attachments",
		params,
		context,
	});
}

/**
 * Get Issue Attachment 課題添付ファイルのダウンロード
 *
 * Downloads issue’s attachment file.
 */
export function getIssueAttachment(
	config: BacklogConfig,
	params: GetIssueAttachmentParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<BacklogResponseFile>> {
	assert(params);
	return requestApiGetFile({
		config,
		path: "issues/:issueIdOrKey/attachments/:attachmentId",
		params,
		context,
	});
}

/**
 * Delete Issue Attachment 課題添付ファイルの削除
 *
 * Returns the list of issue attachments.
 */
export function deleteIssueAttachment(
	config: BacklogConfig,
	params: DeleteIssueAttachmentParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<IssueAttachmentFile>> {
	assert(params);
	return requestApiDelete({
		config,
		path: "issues/:issueIdOrKey/attachments/:attachmentId",
		params,
		context,
	});
}

/**
 * Get Issue Participant List 課題の参加者一覧の取得
 *
 * Returns list of participants of an issue.
 */
export function getIssueParticipantList(
	config: BacklogConfig,
	params: GetIssueParticipantListParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<User[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "issues/:issueIdOrKey/participants",
		params,
		context,
	});
}

/**
 * Get List of Linked Shared Files 課題共有ファイル一覧の取得
 *
 * Returns the list of linked Shared Files to issues.
 */
export function getListOfIssueLinkedSharedFiles(
	config: BacklogConfig,
	params: GetListOfIssueLinkedSharedFilesParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<SharedFile[]>> {
	assert(params);
	return requestApiGet({
		config,
		path: "issues/:issueIdOrKey/sharedFiles",
		params,
		context,
	});
}

/**
 * Link Shared Files to Issue 課題に共有ファイルをリンク
 *
 * Links shared files to issue.
 */
export function linkSharedFilesToIssue(
	config: BacklogConfig,
	params: LinkSharedFilesToIssueParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<SharedFile[]>> {
	assert(params);
	return requestApiPost({
		config,
		path: "issues/:issueIdOrKey/sharedFiles",
		params,
		context,
	});
}

/**
 * Remove Link to Shared File from Issue 課題に共有ファイルをリンク
 *
 * Removes link to shared file from issue.
 */
export function removeLinkToSharedFileFromIssue(
	config: BacklogConfig,
	params: RemoveLinkToSharedFileFromIssueParams,
	context?: BacklogApiContext,
): Promise<BacklogResponse<SharedFile>> {
	assert(params);
	return requestApiDelete({
		config,
		path: "issues/:issueIdOrKey/sharedFiles/:id",
		params,
		context,
	});
}
